import { Listbox, Transition } from '@headlessui/react';

import buildJson from '@/config/build.json';
import {
    getEnvironment,
    portalTitle,
    portalURL,
    xplorSpotLiteTitle,
    xplorSpotTitle
} from '@/helpers/environmentHelper';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import {
    Account,
    selectCredentials,
    selectCurrentAccount,
    selectCurrentAccountName,
    selectCurrentEntityName,
    setCurrentAccount,
    setCurrentAccountName,
    setCurrentEntityName
} from '@/state/reducers/authSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import useXplorPortal from '@/utils/hooks/useXplorPortal';
import { navigateAccordingToAccountType } from '@/utils/navigation';
import { SSOLoadingState, useSso } from '@/utils/providers/SSOProvider';
import { showLayoutSwitch } from '@/utils/route';
import { default as cn } from 'classnames';
import { ButtonHTMLAttributes, Fragment, useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import { LuLayoutDashboard, LuTrendingUp, LuZap } from 'react-icons/lu';
import { MdExpandMore, MdOutlineAccountBox, MdOutlineLogout, MdOutlineSettings } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Divider from '../common/Divider';
import LanguageButton from '../common/LanguageButton';
import { ssoDisabledMessage } from './DesktopNavbar';
import { onLogout, onUserChange } from './Navbar';

const env = getEnvironment();

const MobileDrawer = (props: any) => {
    const { settingsDisclosure, drawerDisclosure } = props;
    const dispatch = useAppDispatch();
    const credentials = useAppSelector(selectCredentials);
    const currentAccount = useAppSelector(selectCurrentAccount);
    const currentAccountName = useAppSelector(selectCurrentAccountName);
    const currentEntityName = useAppSelector(selectCurrentEntityName);

    const lang = useLanguage();
    const navigate = useNavigate();
    const { onXplorPortal } = useXplorPortal(credentials);

    const { entities, isSSOEnabled, isLoadingSSO } = useSso();

    const currentAccountType = useMemo(() => {
        const properties = credentials?.accounts.find((account) => account.code === currentAccount)?.properties || [];
        return properties.find((property) => property.key === 'TYPE')?.value || 'NOP';
    }, [credentials, currentAccount]);

    const convertedAccounts = useMemo(() => {
        return (
            credentials?.accounts?.map((account) => ({
                code: account.code,
                name: account.name,
                properties: account.properties,
                entityName: entities?.find(
                    (entity) =>
                        entity.nameMappings?.find((nameMapping) => nameMapping.source === 'CELER')?.name ===
                        account.name
                )?.entityName
            })) || []
        );
    }, [credentials, entities]);

    const gcexAccountName = useMemo(() => {
        if (entities && !currentEntityName) {
            return (
                entities.find(
                    (entity) =>
                        entity.nameMappings?.find((nameMapping) => nameMapping.source === 'CELER')?.name ===
                        currentAccountName
                )?.entityName || currentAccountName
            );
        } else return currentAccountName;
    }, [entities, currentAccountName, currentEntityName]);

    return (
        <>
            <div
                className={cn(
                    'block lg:hidden h-full fixed z-30 right-0 top-0 w-3/4 sm:w-2/4 bg-brand-background-dark shadow-lg transform transition-transform duration-300 ease-in-out',
                    { ['translate-x-0']: drawerDisclosure[0], ['translate-x-full']: !drawerDisclosure[0] }
                )}>
                {/* <div className="absolute text-brand-neutral-400 right-4 top-4">
                    <MdClose className="w-4 h-4" />
                </div> */}
                {/* Drawer content */}
                <div className="flex flex-col h-full justify-between">
                    <div className="flex flex-col">
                        <div className="flex flex-col">
                            <div className="flex flex-col gap-2 py-4 text-brand-text">
                                <div className="text-brand-neutral-400 text-xs px-4">{lang.portal.account}</div>
                                <Listbox
                                    value={{
                                        code: currentAccount,
                                        name: currentAccountName
                                    }}
                                    onChange={(account: any) => {
                                        dispatch(setCurrentAccount(account.code));
                                        dispatch(setCurrentAccountName(account.name));
                                        dispatch(setCurrentEntityName(account.entityName));
                                        onUserChange(credentials, dispatch);
                                        const curr = credentials?.accounts.find(
                                            (acc) => acc.code === account.code
                                        ) as Account;
                                        navigateAccordingToAccountType(curr, navigate);
                                    }}>
                                    <div className="relative min-w-[150px] w-full">
                                        <Listbox.Button className="w-full">
                                            <div className="flex justify-between items-center px-4 py-2">
                                                <div>
                                                    <MdOutlineAccountBox className="w-5 h-5" />
                                                </div>
                                                <span className="text-sm truncate pl-4">
                                                    {currentEntityName || gcexAccountName}
                                                </span>
                                                <div className="ml-auto">
                                                    <MdExpandMore className="w-5 h-5" />
                                                </div>
                                            </div>
                                        </Listbox.Button>
                                        <Transition
                                            as={Fragment}
                                            leave="transition ease-in duration-100"
                                            leaveFrom="opacity-100"
                                            leaveTo="opacity-0">
                                            {/* temporary shadow to the dropdown to differentiate */}
                                            <Listbox.Options className="absolute z-[60] max-h-60 w-full overflow-auto rounded-md bg-brand-background-dark border border-brand-neutral-700 py-1 text-sm shadow-lg shadow-brand-neutral-900">
                                                {convertedAccounts.map((account, accountIdx) => (
                                                    <Listbox.Option
                                                        key={accountIdx}
                                                        className={({ active }) =>
                                                            cn(
                                                                `relative cursor-pointer select-none flex gap-4 py-2 px-4`,
                                                                {
                                                                    [`bg-brand-primary`]: active
                                                                }
                                                            )
                                                        }
                                                        value={account}>
                                                        <MdOutlineAccountBox className="w-5 h-5" />
                                                        <span className={cn(`block truncate`)}>
                                                            {account.entityName || account.name}
                                                        </span>
                                                    </Listbox.Option>
                                                ))}
                                            </Listbox.Options>
                                        </Transition>
                                    </div>
                                </Listbox>
                            </div>
                            <Divider className="bg-brand-accent-light" />
                        </div>
                        <div className="py-4">
                            {portalURL && (
                                <DrawerItemButton
                                    label={portalTitle}
                                    Icon={LuLayoutDashboard}
                                    onClick={() => onXplorPortal()}
                                    disabled={!isSSOEnabled}
                                    disabledMessage={ssoDisabledMessage(isLoadingSSO)}
                                    isLoadingSSO={isLoadingSSO}
                                    requiresSSO
                                />
                            )}
                            {showLayoutSwitch && !location.pathname.includes('/trader') && (
                                <Fragment>
                                    <DrawerItemButton
                                        label={xplorSpotTitle}
                                        Icon={LuTrendingUp}
                                        onClick={() => navigate('/')}
                                        disabled={currentAccountType !== 'NOP'}
                                        disabledMessage="Margin accounts are not supported in XplorSpot"
                                    />
                                    <DrawerItemButton
                                        label={xplorSpotLiteTitle}
                                        Icon={LuZap}
                                        onClick={() => navigate('/lite')}
                                        disabled={currentAccountType !== 'NOP'}
                                        disabledMessage="Margin accounts are not supported in XplorSpotLite"
                                    />
                                </Fragment>
                            )}
                            <DrawerItemButton
                                label={lang.navigation.settings}
                                Icon={MdOutlineSettings}
                                onClick={settingsDisclosure[1].toggle}
                            />
                        </div>
                    </div>
                    <div className="flex flex-col">
                        <div className="px-4">
                            <LanguageButton />
                        </div>

                        <div className="flex gap-3 p-4 text-brand-text font-semibold text-xs">
                            {env && env !== 'prod' && (
                                <div className="w-fit cursor-default">
                                    <div className="py-1 px-4 rounded-md uppercase bg-brand-primary">{env}</div>
                                </div>
                            )}
                            <div className="py-1 px-4 rounded-md uppercase bg-brand-accent-light">
                                build : {buildJson.build}
                            </div>
                        </div>

                        <Divider className="bg-brand-accent-light" />
                        <div className="flex flex-col gap-2 py-4 text-brand-text">
                            <div className="text-brand-neutral-400 text-xs px-4">{credentials?.username}</div>
                            <DrawerItemButton
                                label={lang.navigation.logout}
                                Icon={MdOutlineLogout}
                                onClick={() => onLogout(credentials, dispatch)}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Overlay */}
            {drawerDisclosure[0] && (
                <div
                    className="block lg:hidden fixed z-20 inset-0 bg-black opacity-50"
                    onClick={drawerDisclosure[1].close}
                />
            )}
        </>
    );
};

export default MobileDrawer;

interface DrawerItemButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    Icon: IconType;
    label: string;
    disabledMessage?: string;
    isLoadingSSO?: SSOLoadingState;
    requiresSSO?: boolean;
}

const DrawerItemButton = (props: DrawerItemButtonProps) => {
    const { Icon, label, disabledMessage, isLoadingSSO = 'idle', requiresSSO = false, ...restProps } = props;
    const { disabled } = restProps;

    const ssoTooltip = useMemo(() => {
        if (isLoadingSSO === 'pending') return 'We are currently connecting this feature via SSO...';
        if (isLoadingSSO === 'error') return 'This feature is not available due to an error in SSO.';
        return '';
    }, [isLoadingSSO]);

    const tooltip = useMemo(() => {
        if (disabled) return ssoTooltip || disabledMessage;
        return ssoTooltip;
    }, [disabled, disabledMessage, ssoTooltip]);

    return (
        <button
            title={tooltip}
            className={cn('group flex w-full gap-4 items-center px-4 py-2 text-sm', {
                'text-brand-neutral-400 cursor-not-allowed': disabled,
                'text-brand-text hover:bg-brand-primary': !disabled
            })}
            {...restProps}>
            <span>
                <Icon className="h-5 w-5" />
            </span>
            <div className="relative flex items-center justify-center">
                {isLoadingSSO === 'pending' && requiresSSO && <Ping isLoadingSSO={isLoadingSSO} />}
                {isLoadingSSO === 'error' && requiresSSO && <Ping isLoadingSSO={isLoadingSSO} />}
                <div
                    className={cn('truncate whitespace-nowrap', {
                        'pl-4': requiresSSO && ['pending', 'error'].includes(isLoadingSSO)
                    })}>
                    {label}
                </div>
            </div>
        </button>
    );
};

export const Ping = ({ isLoadingSSO }: { isLoadingSSO: SSOLoadingState }) => {
    const ping = useMemo(() => {
        switch (isLoadingSSO) {
            case 'pending':
                return {
                    dot: 'bg-yellow-500',
                    wave: 'bg-yellow-400'
                };
            case 'error':
                return {
                    dot: 'bg-brand-red',
                    wave: 'bg-brand-red-light'
                };
            default:
                return false;
        }
    }, [isLoadingSSO]);

    return (
        <Fragment>
            {ping && (
                <Fragment>
                    <span
                        className={cn(
                            'z-[2] absolute top-[5px] left-0 h-2.5 w-2.5 rounded-full opacity-75 animate-ping',
                            {
                                [ping.wave]: true
                            }
                        )}
                    />
                    <span
                        className={cn(
                            'z-[2] absolute top-[5px] left-0 h-2.5 w-2.5 rounded-full border-[1px] border-brand-background text-[6px]',
                            { [ping.dot]: true }
                        )}
                    />
                </Fragment>
            )}
        </Fragment>
    );
};
